import React, { useEffect, useRef } from "react"

type YoutubePlayerProps = {
    videoId: string
    onVideoStarted: (seconds: number, timestamp: Date) => void
    onVideoPaused: (seconds: number, timestamp: Date) => void
    onVideoEnded: (seconds: number, timestamp: Date) => void
    onVideoProgress: (seconds: number, timestamp: Date) => void
}

export default function YoutubePlayer(props: YoutubePlayerProps) {
    const playerRef = useRef<HTMLDivElement | null>(null)
    const ytPlayer = useRef<any>(null)

    const lastTime = useRef<number>(0)
    const isSeeking = useRef<boolean>(false)
    const intervalRef = useRef<NodeJS.Timer | null>(null)

    useEffect(() => {
        const loadYouTubeAPI = () => {
            // @ts-ignore
            if (window.YT && window.YT.Player) {
                initPlayer()
            } else {
                const tag = document.createElement("script")
                tag.src = "https://www.youtube.com/iframe_api"
                document.body.appendChild(tag)
                // @ts-ignore
                window.onYouTubeIframeAPIReady = initPlayer
            }
        }

        const initPlayer = () => {
            // @ts-ignore
            ytPlayer.current = new window.YT.Player(playerRef.current, {
                videoId: props.videoId,
                height: "500",
                width: "100%",
                playerVars: {
                    modestbranding: 1,
                    rel: 0,
                },
                events: {
                    onStateChange: onPlayerStateChange,
                },
            })
        }

        const getVideoWatchedSeconds = () => {
            return ytPlayer.current?.getCurrentTime?.() ?? 0
        }

        const startInterval = () => {
            if (!intervalRef.current) {
                intervalRef.current = setInterval(() => {
                    const currentTime = getVideoWatchedSeconds()
                    const now = new Date()
                    props.onVideoProgress?.(currentTime, now)
                }, 5000)
            }
        }

        const stopInterval = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }

        const onPlayerStateChange = (event: any) => {
            const state = event.data
            const currentTime = getVideoWatchedSeconds()
            const now = new Date()

            const timeDiff = Math.abs(currentTime - lastTime.current)
            if (timeDiff > 1.5) {
                isSeeking.current = true
                setTimeout(() => {
                    isSeeking.current = false
                }, 300)
            }
            lastTime.current = currentTime

            // @ts-ignore
            switch (state) {
                // @ts-ignore
                case window.YT.PlayerState.PLAYING:
                    if (!isSeeking.current) {
                        props.onVideoStarted?.(currentTime, now)
                    }
                    startInterval()
                    break
                // @ts-ignore
                case window.YT.PlayerState.PAUSED:
                    if (!isSeeking.current) {
                        props.onVideoPaused?.(currentTime, now)
                    }
                    stopInterval()
                    break
                // @ts-ignore
                case window.YT.PlayerState.ENDED:
                    props.onVideoEnded?.(currentTime, now)
                    stopInterval()
                    break
            }
        }

        loadYouTubeAPI()

        return () => {
            ytPlayer.current?.destroy?.()
            stopInterval()
        }
    }, [props.videoId])

    return (
        <div
            style={{
                borderRadius: "18px",
                overflow: "hidden",
                minHeight: "500px",
                minWidth: "300px",
            }}
        >
            <div ref={playerRef} />
        </div>
    )
}
